import type { Celula, Orientacao, PalavraColocada, Regiao } from '../tipos';
import { caracteres } from '../lexico/normalizacao';

/**
 * Geometria da grade. Origem no centro conceitual (0, 0); a cadeia cresce em
 * qualquer direção, sem bordas. Coluna cresce para a direita, linha para baixo.
 */

export const CELULA_HORIZONTAL: Celula = { coluna: 1, linha: 0 };
export const CELULA_VERTICAL: Celula = { coluna: 0, linha: 1 };

export function passo(orientacao: Orientacao): Celula {
  return orientacao === 'horizontal' ? CELULA_HORIZONTAL : CELULA_VERTICAL;
}

export function perpendicular(orientacao: Orientacao): Orientacao {
  return orientacao === 'horizontal' ? 'vertical' : 'horizontal';
}

export function celula(coluna: number, linha: number): Celula {
  return { coluna, linha };
}

export function somar(a: Celula, b: Celula): Celula {
  return { coluna: a.coluna + b.coluna, linha: a.linha + b.linha };
}

export function escalar(a: Celula, k: number): Celula {
  return { coluna: a.coluna * k, linha: a.linha * k };
}

export function mesmaCelula(a: Celula, b: Celula): boolean {
  return a.coluna === b.coluna && a.linha === b.linha;
}

/** Célula do i-ésimo caractere de uma palavra que começa em `origem`. */
export function celulaNoIndice(origem: Celula, orientacao: Orientacao, indice: number): Celula {
  return somar(origem, escalar(passo(orientacao), indice));
}

/** Todas as células ocupadas por uma palavra colocada. */
export function celulasDe(palavra: PalavraColocada): readonly Celula[] {
  const total = caracteres(palavra.texto).length;
  const saida: Celula[] = new Array(total);
  for (let i = 0; i < total; i += 1) {
    saida[i] = celulaNoIndice(palavra.origem, palavra.orientacao, i);
  }
  return saida;
}

/**
 * Origem de uma palavra cujo caractere de índice `indiceNoTexto` precisa cair
 * exatamente em `cruzamento`.
 */
export function origemPeloCruzamento(
  cruzamento: Celula,
  orientacao: Orientacao,
  indiceNoTexto: number,
): Celula {
  return somar(cruzamento, escalar(passo(orientacao), -indiceNoTexto));
}

export const REGIAO_VAZIA: Regiao = {
  colunaMin: 0,
  linhaMin: 0,
  colunaMax: 0,
  linhaMax: 0,
};

export function regiaoDeCelulas(celulas: readonly Celula[]): Regiao {
  const primeira = celulas[0];
  if (primeira === undefined) return REGIAO_VAZIA;

  let colunaMin = primeira.coluna;
  let colunaMax = primeira.coluna;
  let linhaMin = primeira.linha;
  let linhaMax = primeira.linha;

  for (const c of celulas) {
    if (c.coluna < colunaMin) colunaMin = c.coluna;
    if (c.coluna > colunaMax) colunaMax = c.coluna;
    if (c.linha < linhaMin) linhaMin = c.linha;
    if (c.linha > linhaMax) linhaMax = c.linha;
  }

  return { colunaMin, colunaMax, linhaMin, linhaMax };
}

export function unirRegioes(a: Regiao, b: Regiao): Regiao {
  return {
    colunaMin: Math.min(a.colunaMin, b.colunaMin),
    colunaMax: Math.max(a.colunaMax, b.colunaMax),
    linhaMin: Math.min(a.linhaMin, b.linhaMin),
    linhaMax: Math.max(a.linhaMax, b.linhaMax),
  };
}

export function expandirRegiao(regiao: Regiao, margem: number): Regiao {
  return {
    colunaMin: regiao.colunaMin - margem,
    colunaMax: regiao.colunaMax + margem,
    linhaMin: regiao.linhaMin - margem,
    linhaMax: regiao.linhaMax + margem,
  };
}

export function regiaoContem(regiao: Regiao, c: Celula): boolean {
  return (
    c.coluna >= regiao.colunaMin &&
    c.coluna <= regiao.colunaMax &&
    c.linha >= regiao.linhaMin &&
    c.linha <= regiao.linhaMax
  );
}

export function regioesSeCruzam(a: Regiao, b: Regiao): boolean {
  return (
    a.colunaMin <= b.colunaMax &&
    a.colunaMax >= b.colunaMin &&
    a.linhaMin <= b.linhaMax &&
    a.linhaMax >= b.linhaMin
  );
}
